import {
  EnumLeadStatus,
  useLeadByIdQuery,
} from "gql/generated";
import { useMemo, useState } from "react";
import { useLoggedInAs } from "shared/hooks";
import { usePeopleContext } from "shared/contextProviders";

type UseLeadModalProps = {
  leadId: string | null;
  onClose: () => void;
};

export const useLeadModal = ({
  leadId,
  onClose,
}: UseLeadModalProps) => {
  const loggedInAs = useLoggedInAs();

  const {
    removeLeadByIdLoading,
    updateLeadStatusByIdLoading,
    removeLeadById,
    updateLeadStatusById,
  } = usePeopleContext();

  const { data: leadByIdData, loading, error } =
    useLeadByIdQuery({
      variables: { id: leadId },
      skip: !leadId,
    });

  const lead = leadByIdData?.leadById ?? undefined;
  const personId = lead?.person?._id ?? "";
  const effectiveLeadId = lead?._id ?? leadId ?? "";

  const canDelete = useMemo(() => {
    if (!loggedInAs || !lead) return false;
    const entityId = loggedInAs.entityId;
    const metadata = lead.metadata;
    return (
      metadata.storefront?._id === entityId ||
      metadata.organization?._id === entityId ||
      metadata.operator?._entityId === entityId
    );
  }, [loggedInAs, lead]);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleDelete = async () => {
    setIsDeleteConfirmOpen(false);
    const success = await removeLeadById(personId, effectiveLeadId);
    if (success) onClose();
  };

  const handleMarkAs = async (status: EnumLeadStatus) => {
    const success = await updateLeadStatusById(personId, effectiveLeadId, status);
    if (success) onClose();
  };

  return {
    lead,
    loading,
    removeLeadByIdLoading,
    updateLeadStatusByIdLoading,
    error,
    canDelete,
    isDeleteConfirmOpen,
    setIsDeleteConfirmOpen,
    handleDelete,
    handleMarkAs,
  };
};
